import { useState, useRef, useEffect, useCallback } from "react";
import { MessageCircle, X, Send, Trash2, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const API_BASE = "/api/chatbot";

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Load session from localStorage
  useEffect(() => {
    const stored = localStorage.getItem("amdecor_chat_session");
    if (stored) {
      try {
        const { sessionId: sid, messages: msgs } = JSON.parse(stored);
        setSessionId(sid);
        setMessages(msgs || []);
      } catch {
        // Invalid stored data, start fresh
      }
    }
  }, []);

  // Save session to localStorage
  useEffect(() => {
    if (sessionId && messages.length > 0) {
      localStorage.setItem(
        "amdecor_chat_session",
        JSON.stringify({ sessionId, messages })
      );
    }
  }, [sessionId, messages]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);
    setIsLoading(true);

    // Add placeholder for assistant response
    setMessages((prev) => [...prev, { role: "assistant", content: "" }]);

    try {
      const params = new URLSearchParams({ message: userMessage });
      if (sessionId) params.append("session_id", sessionId);

      const response = await fetch(`${API_BASE}/chat/stream?${params}`);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      let assistantContent = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.type === "session") {
                setSessionId(data.session_id);
              } else if (data.type === "delta") {
                assistantContent += data.content;
                setMessages((prev) => {
                  const newMsgs = [...prev];
                  newMsgs[newMsgs.length - 1] = {
                    role: "assistant",
                    content: assistantContent,
                  };
                  return newMsgs;
                });
              } else if (data.type === "error") {
                setMessages((prev) => {
                  const newMsgs = [...prev];
                  newMsgs[newMsgs.length - 1] = {
                    role: "assistant",
                    content: data.message || "Sorry, an error occurred.",
                  };
                  return newMsgs;
                });
              }
            } catch {
              // Skip invalid JSON
            }
          }
        }
      }
    } catch (error) {
      console.error("Chat error:", error);
      setMessages((prev) => {
        const newMsgs = [...prev];
        newMsgs[newMsgs.length - 1] = {
          role: "assistant",
          content: "Sorry, I couldn't connect. Please try again.",
        };
        return newMsgs;
      });
    } finally {
      setIsLoading(false);
    }
  };

  const clearChat = async () => {
    if (sessionId) {
      try {
        await fetch(`${API_BASE}/chat/${sessionId}`, { method: "DELETE" });
      } catch {
        // Ignore errors when clearing
      }
    }
    setMessages([]);
    setSessionId(null);
    localStorage.removeItem("amdecor_chat_session");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Floating Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-24 right-6 z-50 w-14 h-14 bg-brand-gold hover:bg-brand-gold/90 text-brand-dark rounded-full shadow-lg flex items-center justify-center transition-colors"
            data-testid="chat-widget-button"
            aria-label="Open chat"
          >
            <MessageCircle className="w-6 h-6" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-24 right-6 z-50 w-[360px] max-w-[calc(100vw-48px)] h-[500px] max-h-[calc(100vh-150px)] bg-brand-dark border border-brand-gold/20 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
            data-testid="chat-widget-window"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-brand-gold/10 border-b border-brand-gold/20">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-brand-gold rounded-full flex items-center justify-center">
                  <MessageCircle className="w-4 h-4 text-brand-dark" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-brand-text">
                    AM Decor Assistant
                  </h3>
                  <p className="text-xs text-brand-text-secondary">
                    Ask about products & pricing
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {messages.length > 0 && (
                  <button
                    onClick={clearChat}
                    className="p-2 text-brand-text-secondary hover:text-brand-text rounded-lg transition-colors"
                    title="Clear chat"
                    data-testid="chat-clear-button"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 text-brand-text-secondary hover:text-brand-text rounded-lg transition-colors"
                  data-testid="chat-close-button"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 && (
                <div className="text-center py-8">
                  <div className="w-12 h-12 bg-brand-gold/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <MessageCircle className="w-6 h-6 text-brand-gold" />
                  </div>
                  <p className="text-sm text-brand-text-secondary mb-4">
                    Hi! I'm here to help with questions about our products,
                    pricing, and turnaround times.
                  </p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {[
                      "Canvas print sizes?",
                      "Turnaround times?",
                      "Vehicle wrap cost?",
                    ].map((q) => (
                      <button
                        key={q}
                        onClick={() => {
                          setInput(q);
                          setTimeout(() => inputRef.current?.focus(), 50);
                        }}
                        className="text-xs px-3 py-1.5 bg-brand-gold/10 text-brand-gold rounded-full hover:bg-brand-gold/20 transition-colors"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${
                    msg.role === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  <div
                    className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm ${
                      msg.role === "user"
                        ? "bg-brand-gold text-brand-dark rounded-br-md"
                        : "bg-brand-gold/10 text-brand-text rounded-bl-md"
                    }`}
                  >
                    {msg.content || (
                      <Loader2 className="w-4 h-4 animate-spin text-brand-gold" />
                    )}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-brand-gold/20">
              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type your question..."
                  disabled={isLoading}
                  className="flex-1 px-4 py-2.5 bg-brand-gold/5 border border-brand-gold/20 rounded-xl text-sm text-brand-text placeholder:text-brand-text-secondary focus:outline-none focus:border-brand-gold/40 disabled:opacity-50"
                  data-testid="chat-input"
                />
                <button
                  onClick={sendMessage}
                  disabled={!input.trim() || isLoading}
                  className="p-2.5 bg-brand-gold text-brand-dark rounded-xl hover:bg-brand-gold/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="chat-send-button"
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
